import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  CheckIcon, 
  LockClosedIcon, 
  CalendarDaysIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ArrowUpTrayIcon,
  XCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { Popover } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SEO from '../components/SEO';
import FAQAccordion from '../components/FAQAccordion';
import { redirectToStripeCheckout } from '../lib/stripeCheckout';
import { useSession } from '@supabase/auth-helpers-react';
import toast from 'react-hot-toast';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';
import { getLTDParentsPriceId } from '../lib/stripeCheckout';
import { supabase } from '../lib/supabase';

export default function LTDParentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const queryClient = useQueryClient();

  // Check if user already has lifetime access
  const { data: hasLifetimeAccess, isLoading: checkingAccess } = useQuery(
    ['lifetime-access', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('lifetime_access, subscription_plan, subscription_status')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Error checking lifetime access:', error);
        return false;
      }

      return (
        profile?.lifetime_access === true ||
        (profile?.subscription_plan === 'ltd-parents' && profile?.subscription_status === 'lifetime')
      );
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  // When logged out, we're not checking, so set checkingAccess to false
  const isActuallyChecking = checkingAccess && !!session?.user?.id;

  const cancelledToastShownRef = useRef(false);

  // Handle cancel redirects (success now goes to /payment/success)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cancelled = params.get('cancelled');
    const success = params.get('success');

    // If success=true is in URL, redirect to proper success page
    if (success === 'true') {
      const sessionId = params.get('session_id');
      navigate(`/payment/success?session_id=${sessionId || ''}&plan_name=ltd-parents`, { replace: true });
      return;
    }

    if (cancelled === 'true') {
      // Clear any checkout flags
      sessionStorage.removeItem('stripe_checkout_initiated');
      sessionStorage.removeItem('ltd_checkout_intent');
      
      // Invalidate the lifetime access query to ensure fresh data
      queryClient.invalidateQueries(['lifetime-access', session?.user?.id]);
      
      if (!cancelledToastShownRef.current) {
        cancelledToastShownRef.current = true;
        toast.error('Payment cancelled. You can try again when ready.', {
          duration: 5000,
          icon: '❌',
        });
        track('payment_cancelled', {
          persona: 'parent',
          page: 'ltd_parents',
          plan: 'ltd-parents',
        });
      }
      // Clean up URL
      navigate('/ltd/parents', { replace: true });
    } else {
      cancelledToastShownRef.current = false;
    }
    
    // Also check if user returned from Stripe without completing (sessionStorage flag)
    const wasInCheckout = sessionStorage.getItem('stripe_checkout_initiated');
    if (wasInCheckout && !cancelled && !success) {
      // User came back but no cancelled/success param - likely cancelled
      sessionStorage.removeItem('stripe_checkout_initiated');
      sessionStorage.removeItem('ltd_checkout_intent');
      
      // Invalidate query to ensure we don't show stale success state
      queryClient.invalidateQueries(['lifetime-access', session?.user?.id]);
      
      toast('Payment not completed. You can try again when ready.', {
        duration: 5000,
        icon: 'ℹ️',
      });
    }
  }, [location.search, navigate, session?.user?.id, queryClient]);

  // Remove excessive bottom padding from main element
  useEffect(() => {
    const mainElement = document.querySelector('main[role="main"]') as HTMLElement;
    if (mainElement) {
      mainElement.style.paddingBottom = '0';
      return () => {
        mainElement.style.paddingBottom = '';
      };
    }
  }, []);

  // Track page view
  useEffect(() => {
    trackEvent('ltd_parents_page_view', {
      is_authenticated: !!session,
    });
  }, [session]);

  const handleGetLifetimeAccess = async () => {
    // Prevent purchase if user already has lifetime access
    if (hasLifetimeAccess) {
      toast.success('You already have lifetime access!', {
        duration: 3000,
        icon: '✅',
      });
      navigate('/kids-schedule');
      return;
    }

    trackEvent('ltd_parents_cta_clicked', {
      cta_location: 'hero',
      is_authenticated: !!session,
    });
    track('ltd_parents_cta_clicked', {
      persona: 'parent',
      page: 'ltd_parents',
      cta_location: 'hero',
      is_authenticated: !!session,
    });

    // If not logged in, redirect to registration
    if (!session) {
      trackEvent('ltd_parents_checkout_blocked_no_auth', {
        redirect_to: 'register',
      });
      track('ltd_parents_checkout_blocked_no_auth', {
        persona: 'parent',
        page: 'ltd_parents',
        redirect_to: 'register',
      });
      // Store checkout intent in sessionStorage for post-login redirect
      sessionStorage.setItem('ltd_checkout_intent', 'ltd-parents');
      navigate('/register?type=parent&plan=ltd-parents');
      return;
    }

    const priceId = getLTDParentsPriceId();
    if (!priceId) {
      const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
      const envVarName = isDev 
        ? 'VITE_STRIPE_LTD_PARENTS_PRICE_ID_DEV' 
        : 'VITE_STRIPE_LTD_PARENTS_PRICE_ID';
      const envVarValue = isDev
        ? import.meta.env.VITE_STRIPE_LTD_PARENTS_PRICE_ID_DEV
        : import.meta.env.VITE_STRIPE_LTD_PARENTS_PRICE_ID;
      
      console.error('LTD Parents Price ID not configured:', {
        environment: isDev ? 'development' : 'production',
        envVarName,
        envVarExists: envVarValue !== undefined,
        envVarValue: envVarValue || '(empty)',
        isDev,
        nodeEnv: import.meta.env.NODE_ENV,
      });
      
      toast.error('Payment system not configured. Please contact support.', {
        duration: 5000,
      });
      return;
    }

    try {
      trackEvent('ltd_parents_checkout_initiated', {
        is_authenticated: true,
      });
      track('ltd_parents_checkout_initiated', {
        persona: 'parent',
        page: 'ltd_parents',
        is_authenticated: true,
      });

      await redirectToStripeCheckout({
        priceId,
        planName: 'ltd-parents',
        userEmail: session?.user?.email,
      });
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      trackEvent('ltd_parents_checkout_error', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      track('ltd_parents_checkout_error', {
        persona: 'parent',
        page: 'ltd_parents',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to start checkout. Please try again.',
        { duration: 5000 }
      );
    }
  };

  const faqs = [
    {
      '@type': 'Question' as const,
      name: 'Is this really lifetime?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: 'Yes. One payment. No subscriptions.',
      },
    },
    {
      '@type': 'Question' as const,
      name: 'Will you add more features?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: 'Yes — calendar sharing, reminders, and more.',
      },
    },
    {
      '@type': 'Question' as const,
      name: 'Can I use it every year?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: "That's the point.",
      },
    },
  ];

  // Prepare structured data for SEO
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "CampWhiz for Parents - $79 Lifetime Deal | Founding Parents",
      "description": "Stop juggling camp spreadsheets every year. Plan, compare, and organize your kids' camps in one place — for life. $79 one-time, lifetime access.",
      "url": "https://campwhiz.com/ltd/parents",
      "publisher": {
        "@type": "Organization",
        "name": "CampWhiz",
        "url": "https://campwhiz.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://campwhiz.com/assets/campwhiz-logo-color2.svg"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "CampWhiz Founding Parents - Lifetime Access",
      "description": "Lifetime access to CampWhiz for parents. Plan, compare, and organize your kids' camps — for life. One-time payment, no subscriptions.",
      "brand": {
        "@type": "Brand",
        "name": "CampWhiz"
      },
      "offers": {
        "@type": "Offer",
        "url": "https://campwhiz.com/ltd/parents",
        "priceCurrency": "USD",
        "price": "79.00",
        "priceValidUntil": "2026-12-31",
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition",
        "description": "Lifetime access - one-time payment, no renewals"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.name,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.acceptedAnswer.text
        }
      }))
    }
  ];

  return (
    <div className="page-container" style={{ minHeight: 'auto', paddingBottom: 0 }}>
      <SEO
        title="CampWhiz for Parents - $79 Lifetime Deal | Founding Parents"
        description="Stop juggling camp spreadsheets every year. Plan, compare, and organize your kids' camps in one place — for life. $79 one-time, lifetime access."
        keywords="camp planning, lifetime deal, camp organizer, family camp calendar, campwhiz parents"
        url="/ltd/parents"
        structuredData={structuredData}
      />

      <div className="page-content">
        {/* Hero Section - Improved hierarchy */}
        <div className="mb-20 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-gray-900 mb-6 leading-tight">
              Stop juggling camp spreadsheets every year.
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 mb-12 leading-relaxed max-w-3xl mx-auto">
              Plan, compare, and organize your kids' camps — once — your hub for all season camp planning.
            </p>
            
            {/* Hero CTA - Left aligned for better hierarchy */}
            <div className="flex flex-col items-center mb-8">
              <div className="text-5xl sm:text-6xl font-bold text-primary mb-3">
                <span className="text-3xl sm:text-4xl font-normal align-super">$</span>79 one-time
              </div>
              <div className="text-xl text-gray-600 mb-8 flex items-center justify-center gap-2">
                Lifetime access
                <Popover className="relative inline-flex items-center">
                  <Popover.Button className="focus:outline-none flex items-center">
                    <QuestionMarkCircleIcon className="h-6 w-6 text-primary cursor-help transition-colors hover:text-primary/80 mt-0.5" />
                  </Popover.Button>
                  <Popover.Panel className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50 w-80 sm:w-96">
                    <div className="px-5 py-4 text-sm text-white bg-gray-900 rounded-lg shadow-xl">
                      <p className="whitespace-normal text-left leading-relaxed">
                        You'll be a founding member and help shape CampWhiz with your feedback. Your early support makes this partnership possible.
                      </p>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-2">
                        <div className="border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </Popover.Panel>
                </Popover>
              </div>
              {hasLifetimeAccess ? (
                <div className="mb-3">
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg px-6 py-4 mb-3">
                    <div className="flex items-center justify-center gap-2 text-green-800 font-semibold mb-2">
                      <CheckIcon className="h-6 w-6" />
                      <span>You have lifetime access!</span>
                    </div>
                    <Link
                      to="/kids-schedule"
                      className="btn btn-primary btn-lg px-10 py-5 text-xl font-semibold"
                    >
                      Go to Kids Schedule
                    </Link>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleGetLifetimeAccess}
                  disabled={isActuallyChecking}
                  className="btn btn-primary btn-lg px-10 py-5 text-xl font-semibold mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-umami-event="LTD-Parents-CTA-Hero"
                  data-umami-event-plan="ltd-parents"
                >
                  {isActuallyChecking ? 'Checking...' : 'Get Lifetime Access'}
                </button>
              )}
              <p className="text-sm text-gray-900 mb-1">
                Takes less than 5 minutes to set up
              </p>
              <p className="text-xs text-gray-600">
                Built by parents. No subscriptions. No spam.
              </p>
            </div>
          </div>
        </div>

        {/* Concrete Parent Scenario - Typography-focused */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">
            Parents know this
          </h2>
          <div className="bg-primary rounded-2xl p-8 sm:p-12 shadow-sm">
            <div className="text-center space-y-4">
              <p className="text-3xl sm:text-4xl text-white font-bold">
                Two kids.
              </p>
              <p className="text-2xl sm:text-3xl text-white font-light italic">
                Three camps each.
              </p>
              <p className="text-2xl sm:text-3xl text-white font-medium">
                Different weeks. Different hours.
              </p>
              <p className="text-3xl sm:text-4xl text-white font-bold mt-6">
                CampWhiz keeps it all in one calendar — your hub for all season camp planning.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Section 1 - Image Left, Text Right */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gray-100 rounded-2xl aspect-[940/720] overflow-hidden flex items-center justify-center max-w-md mx-auto">
                <video
                  className="w-full h-full object-contain"
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src="/assets/demos/cw-favorite-feature-demo.webm" type="video/webm" />
                  <source src="/assets/demos/cw-favorite-feature-demo.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Save camps once.<br />Reuse every year.
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Add your favorite camps to your library. They're saved forever, so you never have to search for them again.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">Compare dates, ages, and formats</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">Build your season calendar</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">No re-starting from zero</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Feature Section 2 - Text Left, Image Right */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                View calendars in one place and share with the entire family.
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Build your kids' schedules in one place and view your Family Camp Calendar. Share a live calendar with co-parents and caregivers so everyone stays in sync.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">Multiple kids, one calendar</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">Share with co-parents & caregivers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg font-semibold text-primary">Auto-updates across all devices</span>
                </li>
              </ul>
            </div>
            <div>
              <div className="bg-gray-100 rounded-2xl aspect-[940/720] overflow-hidden flex items-center justify-center max-w-md mx-auto">
                <video
                  className="w-full h-full object-contain"
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src="/assets/demos/cw-schedule-feature-demo.webm" type="video/webm" />
                  <source src="/assets/demos/cw-schedule-feature-demo.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>

        {/* Who This Is For / Not For - Bold cards */}
        <div className="max-w-5xl mx-auto mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border-2 border-green-500 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Perfect for you if:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-semibold">You plan camps more than once</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-semibold">You have multiple kids</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-semibold">You hate re-doing work every season</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-red-500 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Not for you if:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-semibold">You only do one camp, one time</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-semibold">You enjoy spreadsheets (no judgment)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Lifetime Deal Section - FIXED: White card with purple accents */}
        <div className="max-w-2xl mx-auto mb-20">
          <div className="bg-white rounded-2xl p-8 sm:p-10 text-center border-2 border-primary shadow-sm">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 text-center">Founding Parents Lifetime Access</h2>
            <div className="mb-8">
              <div className="flex items-center justify-center gap-4 mb-2">
                <div className="text-xl text-gray-400 line-through">$60 / year</div>
                <div className="text-5xl font-bold text-primary">
                  <span className="text-3xl font-normal align-super">$</span>79 one-time
                </div>
              </div>
            </div>
            <ul className="space-y-3 mb-8 text-left max-w-md mx-auto">
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">Lifetime access</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">All current parent features</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">Future parent features included</span>
              </li>
            </ul>
            {/* Emotional line - Never rebuild again */}
            <p className="text-xl font-semibold mb-6 text-gray-900">
              Never rebuild your camp system again.
            </p>
            {/* Risk reversal */}
            <p className="text-sm text-gray-600 mb-8 italic">
              If this doesn't simplify your camp planning, email us — we'll make it right.
            </p>
            {hasLifetimeAccess ? (
              <div className="mb-3">
                <div className="bg-green-50 border-2 border-green-500 rounded-lg px-6 py-4 mb-3">
                  <div className="flex items-center justify-center gap-2 text-green-800 font-semibold mb-2">
                    <CheckIcon className="h-6 w-6" />
                    <span>You have lifetime access!</span>
                  </div>
                  <Link
                    to="/kids-schedule"
                    className="btn btn-primary btn-lg px-10 py-5 text-xl font-semibold"
                  >
                    Go to Kids Schedule
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleGetLifetimeAccess}
                  disabled={isActuallyChecking}
                  className="btn btn-primary btn-lg px-10 py-5 text-xl font-semibold mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-umami-event="LTD-Parents-CTA-Middle"
                  data-umami-event-plan="ltd-parents"
                >
                  {isActuallyChecking ? 'Checking...' : 'Get Lifetime Access'}
                </button>
                <p className="text-sm text-gray-500">
                  Takes less than 5 minutes to set up
                </p>
              </>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <FAQAccordion faqs={faqs} campaign="ltd-parents" />
        </div>

        {/* Free CTA Section */}
        {!hasLifetimeAccess && (
          <div className="max-w-4xl mx-auto mb-16 text-center">
            <div className="bg-gray-50 rounded-2xl p-8 sm:p-10 border-2 border-gray-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Not ready to spend $? No problem!
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Register and search and save your favorite camps.
              </p>
              <Link
                to="/register?type=parent"
                className="btn btn-secondary btn-lg px-8 py-4 text-lg font-semibold inline-block"
                data-umami-event="LTD-Parents-Free-CTA-Clicked"
                data-umami-event-plan="ltd-parents"
                onClick={() => {
                  trackEvent('ltd_parents_free_cta_clicked', {
                    cta_location: 'bottom',
                    destination: 'register',
                    user_type: 'parent',
                  });
                  track('ltd_parents_free_cta_clicked', {
                    persona: 'parent',
                    page: 'ltd_parents',
                    cta_location: 'bottom',
                    destination: 'register',
                  });
                }}
              >
                Start FREE →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

